import React, { useEffect, useState, useRef } from 'react';
type Reaction = {
  id: number;
  emoji: string;
  left: number; // % from left within the overlay
  size: number; // px
  duration: number; // s
  drift: number; // px horizontal wobble amplitude
  opacity: number;
};
const EMOJIS = ['❤️', '😍', '🔥', '👏'];
/**
 * YouTube/IG-live style floating reactions. Emojis spawn near the bottom and
 * rise to the top with a gentle horizontal wobble while fading out. Purely
 * decorative — pointer-events disabled so it never blocks the video.
 *
 * Cadence: instead of one big mixed burst, reactions arrive in natural WAVES
 * roughly every 30s. Within a wave they come grouped — a little cluster of one
 * emoji floats up first, then a cluster of the next emoji, and so on — so it
 * reads like real reactions rolling in rather than all at once.
 */
type LiveReactionsProps = {
  /** Only spawn new reactions while true (e.g. while the video is playing). */
  active?: boolean;
};
export function LiveReactions({
  active = true
}: LiveReactionsProps) {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const idRef = useRef(0);
  useEffect(() => {
    if (!active) return;
    // Every scheduled timeout is tracked so we can clear them all on cleanup.
    const timers = new Set<number>();
    const after = (ms: number, fn: () => void) => {
      const t = window.setTimeout(() => {
        timers.delete(t);
        fn();
      }, ms);
      timers.add(t);
      return t;
    };
    const spawnOne = (emoji: string) => {
      const id = idRef.current++;
      const reaction: Reaction = {
        id,
        emoji,
        left: 60 + Math.random() * 35,
        size: 12 + Math.random() * 16,
        duration: 3.2 + Math.random() * 2.3,
        drift: (Math.random() - 0.5) * 60,
        opacity: 0.65 + Math.random() * 0.35 // 0.65–1
      };
      setReactions(prev => [...prev.slice(-24), reaction]);
      // Remove after it finishes animating.
      after(reaction.duration * 1000 + 200, () => {
        setReactions(prev => prev.filter(r => r.id !== id));
      });
    };
    // One wave = clusters of each emoji, one emoji after another. The emoji
    // order is shuffled per wave so it never looks looped.
    const runWave = () => {
      const order = [...EMOJIS].sort(() => Math.random() - 0.5);
      let offset = 0;
      order.forEach(emoji => {
        const count = 3 + Math.floor(Math.random() * 3); // 3–5 of this emoji
        for (let i = 0; i < count; i++) {
          after(offset, () => spawnOne(emoji));
          offset += 180 + Math.random() * 220; // stagger within the cluster
        }
        offset += 500 + Math.random() * 400; // gap before the next emoji
      });
      // Schedule the next wave ~30s after this one finishes emitting.
      after(offset + 30000, runWave);
    };
    // Hold the first wave back ~30s so reactions don't dump in the instant
    // playback starts; every wave after this one is already spaced ~30s apart.
    const startTimer = after(30000, runWave);
    return () => {
      window.clearTimeout(startTimer);
      timers.forEach(t => window.clearTimeout(t));
      timers.clear();
    };
  }, [active]);
  return <div className="pointer-events-none absolute inset-0 overflow-hidden z-10" data-id="element-159" data-mp="1uakcm7jq8czz">
      <style data-id="element-160" data-mp="1ucwu6jlkolci">{`@keyframes liveFloatUp{0%{transform:translateY(0) translateX(0) scale(.85);opacity:0}10%{opacity:1}55%{transform:translateY(-55%) translateX(var(--drift)) scale(1.05)}100%{transform:translateY(-110%) translateX(calc(var(--drift) * -0.6)) scale(.9);opacity:0}}`}</style>
      {reactions.map(r => <span key={r.id} aria-hidden="true" className="absolute bottom-3 will-change-transform select-none" style={{
      left: `${r.left}%`,
      fontSize: `${r.size}px`,
      opacity: r.opacity,
      // @ts-expect-error custom property
      '--drift': `${r.drift}px`,
      animation: `liveFloatUp ${r.duration}s ease-in forwards`,
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))'
    }} data-id="element-161" data-mp="1qas5lz9zeewq">
          {r.emoji}
        </span>)}
    </div>;
}